// The `format_prompt` deprecation, in one place.
//
// `score_gen.format_prompt` is the prose half of the output contract, and it is going away in favour
// of `output_extraction.output_schema` — which is enforced rather than requested: the scoring call
// carries `response_format={type: json_schema, strict: true}`, so the model cannot emit malformed
// JSON at all.
//
// The retirement is a TWO-POSITION SWITCH rather than a flag day, and the reason matters. Create and
// PATCH accept prose today; the only rule is that exactly one contract is declared. A client that
// refused to save prose before the API refuses it would be blocking work for a rule that does not
// exist yet — the worst kind of strictness. So:
//
//   * `deprecated` (now) — prose is authorable NOWHERE, still editable where it already exists, and
//     every surface says it is on the way out. Validation calls it ADVICE.
//   * `retired` (when create rejects it) — prose becomes a save-tier ERROR and `toPayload` drops the
//     key whenever a schema is present.
//
// Flipping is this one constant. Nothing else tests the state directly, so the flip cannot be
// half-applied.
//
// The read path is NOT part of the deprecation and never becomes dead code: the loader stays tolerant
// of `format_prompt` (approved specs are frozen and must keep loading), so this client keeps meeting
// prose in stored specs indefinitely. Deprecating the write door is not the same as forgetting how to
// read.

export const PROSE_CONTRACT_STATES = { DEPRECATED: 'deprecated', RETIRED: 'retired' };

// Blocked on the backend, in this order: the model-capability map lands (nothing currently gates
// structured outputs on the model, so flipping first can push specs onto a path that 400s every
// request), the existing prose specs are migrated, then create and PATCH reject the key — they share
// one validator, so they flip together.
export const PROSE_CONTRACT_STATE = PROSE_CONTRACT_STATES.DEPRECATED;

export const isProseRetired = () => PROSE_CONTRACT_STATE === PROSE_CONTRACT_STATES.RETIRED;

// The one sentence every surface uses for a spec that still declares prose, so the deprecation does
// not get described three different ways across three screens.
export const PROSE_LEGACY_NOTE = isProseRetired()
  ? 'A prose output contract is no longer accepted on save. This spec keeps running — the loader is tolerant — but it cannot be saved again until it declares an output_schema instead.'
  : 'A prose output contract is deprecated. It still saves and still runs; new specs declare an output_schema, which is enforced by the model’s grammar rather than requested in prose.';

// What validate.js files for a spec whose declared contract is prose. Severity and tier both move
// with the switch; the wording says which door is which either way.
export const proseDeprecationRow = () =>
  isProseRetired()
    ? {
        title: '`format_prompt` is no longer accepted',
        explain:
          'Create and PATCH reject a prose output contract. The loader still tolerates it, so a spec already stored this way keeps running — but this draft cannot be saved until it declares an `output_schema` instead.',
      }
    : {
        title: '`format_prompt` is deprecated',
        explain:
          'The prose contract is followed best-effort; an `output_schema` is enforced by the model’s grammar, which is what removes the malformed-JSON re-score tax. This still saves and still runs.',
      };
